"use client";
import {
  ButtonComponent,
  LoaderComponent,
  ModalComponent,
  PaperComponent,
  PrivacyComponent,
} from "@/components";
import ModalAlert from "@/components/Modals/ModalAlert";
import {
  GET_PRODUCT_APPLICATION,
  IMAGES,
  PRODUCT_HANDLER,
  STAGE,
  STAGE_BASED_ROUTING,
  VIDEO_KYC_INSTRUCTIONS,
  WEB_COLORS,
  WEB_EVENTS,
} from "@/lib/constants";
import ROUTES from "@/lib/constants/PageRoutes";
import { useFooter } from "@/lib/context/FooterContext";
import useErrorHandling from "@/lib/hooks/errorhandling";
import { sendEventAnalytics } from "@/lib/hooks/eventAnalytics";
import useLogoutHook from "@/lib/hooks/logoutHook";
import {
  useGetApplicationQuery,
  useGetCustomerQuery,
  useInitiateVkycMutation,
  useVkycDetailsQuery,
} from "@/lib/redux";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { Grid, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

const VideoKyc = ({ product }) => {
  const { setActions } = useFooter();

  const time = () => {
    const date = new Date();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    // hours %= 12;
    // hours = hours ? hours : 12;
    return `${hours}:${minutes.toString().padStart(2, "0")} ${ampm}`;
  };
  const router = useRouter();
  const [scheduledModal, setScheduledModal] = useState(false);
  const [openModalAlert, setOpenModalAlert] = useState(false);
  const [modalDescription, setModalDescription] = useState("");
  const t = useTranslations("videoKyc");
  const {
    data: applicationData,
    isLoading: applicationLoading,
    error: errorGetApplication,
    refetch: refetchApplication,
  } = useGetApplicationQuery("", { refetchOnMountOrArgChange: true });
  const productApplication = GET_PRODUCT_APPLICATION(
    PRODUCT_HANDLER[product],
    applicationData?.data,
  );
  const { data: customerData, isLoading: customerLoading } =
    useGetCustomerQuery();

  const [
    createVKYC,
    {
      data: createVkycData,
      error: errorCreateVkycData,
      isLoading: createVkycDataLoading,
    },
  ] = useInitiateVkycMutation();
  const {
    data: vkycDetailsData,
    error: errorVkycDetailsData,
    isLoading: vkycDetailsLoading,
    refetch: refetchVkycDetails,
  } = useVkycDetailsQuery(
    { appId: productApplication?.appId },
    { refetchOnMountOrArgChange: true, skip: !productApplication?.appId },
  );

  const error =
    errorCreateVkycData || errorVkycDetailsData || errorGetApplication;
  const vkycHandling = () => {
    if (vkycDetailsData?.data?.vkycHoliday) {
      setModalDescription(vkycDetailsData?.data?.vkycMessage);
      setScheduledModal(true);
      return;
    }
    if (vkycDetailsData?.data?.vkycApplicationStatus === "needs_review") {
      setModalDescription(vkycDetailsData?.data?.vkycMessage);
      setScheduledModal(true);
      return;
    } else {
      const productApplication = GET_PRODUCT_APPLICATION(
        PRODUCT_HANDLER[product],
        applicationData?.data,
      );
      createVKYC({
        appId: productApplication?.appId,
        deviceType: "web",
      });
    }
  };

  useEffect(() => {
    if (createVkycData?.data?.result) {
      sendEventAnalytics({
        eventName: WEB_EVENTS.VKYC_STARTED,
        ...productApplication,
        ...customerData?.data,
      });
      window.location.href = `${createVkycData?.data?.result}`;
    }
    if (createVkycData?.data?.stage === STAGE.DIGI_KYC) {
      setOpenModalAlert(true);
    }
  }, [createVkycData]);

  useEffect(() => {
    if (applicationData?.data) {
      const productApplication = GET_PRODUCT_APPLICATION(
        PRODUCT_HANDLER[product],
        applicationData?.data,
      );
      if (
        productApplication?.stage &&
        productApplication?.stage !== STAGE?.VKYC &&
        productApplication?.stage !== STAGE?.VKYC_SCHEDULED &&
        productApplication?.stage !== STAGE?.RPD_MODE &&
        !sessionStorage?.vkycFrom
      ) {
        if (productApplication?.stage == STAGE?.E_SIGN) {
          router.push(ROUTES(product).E_SIGN_INSTRUCTIONS);
          return;
        }

        router.push(STAGE_BASED_ROUTING(productApplication?.stage));
      }
      if (
        productApplication?.stage &&
        productApplication?.stage !== STAGE?.RPD_MODE
      ) {
        if (productApplication?.stage == STAGE?.E_SIGN) {
          router.push(ROUTES(product).E_SIGN_INSTRUCTIONS);
          return;
        }
        router.push(STAGE_BASED_ROUTING(productApplication?.stage));
      }
    }
  }, [applicationData?.data]);
  useEffect(() => {
    setActions({
      showReject: true,
      showConfirm: true,
      onConfirm: vkycHandling,
      confirmBtnLabel: "Proceed",
      onReject: () =>
        productApplication?.stage === STAGE.RPD_MODE
          ? router.push(STAGE_BASED_ROUTING(productApplication?.stage))
          : router.push("/"),
    });
    if (
      vkycDetailsData?.data?.vkycHoliday ||
      vkycDetailsData?.data?.vkycApplicationStatus === "needs_review"
    ) {
      setModalDescription(vkycDetailsData?.data?.vkycMessage);
      setScheduledModal(true);
      return;
    }
  }, [productApplication, vkycDetailsData]);
  const isLoading =
    vkycDetailsLoading ||
    createVkycDataLoading ||
    customerLoading ||
    applicationLoading;
  useErrorHandling(error);

  return (
    <Stack rowGap={2} mb={2}>
      <LoaderComponent open={isLoading} />
      <PaperComponent
        elevation={0}
        paperStyle={{ backgroundColor: "rgba(242, 247, 247, 0.6)" }}
      >
        <Stack p={2}>
          <Typography
            fontWeight={700}
            fontSize={{ xs: 20, sm: 24 }}
            color={WEB_COLORS.primary}
          >
            {t("tittle")}
          </Typography>
          <Typography
            fontSize={{ xs: 14, sm: 16 }}
            color={WEB_COLORS.fadeColor}
          >
            {t("subTittle")}
          </Typography>
        </Stack>
        <Stack p={2} gap={4}>
          <Stack p={2} gap={4}>
            <Grid container rowGap={3} spacing={2}>
              {VIDEO_KYC_INSTRUCTIONS().map(({ title, icon, text }) => (
                <Grid item key={title} xs={12} md={6}>
                  <Stack spacing={2} direction={"row"} alignItems={"center"}>
                    <Image height={60} width={60} src={icon} alt="title" />
                    <Stack>
                      <Typography
                        fontSize={{ xs: 12, sm: 16 }}
                        fontWeight={500}
                        color={WEB_COLORS.primary}
                      >
                        {title}
                      </Typography>
                      <Typography
                        fontSize={{ xs: 12, sm: 14 }}
                        color={WEB_COLORS.fadeColor}
                        fontWeight={400}
                      >
                        {text}
                      </Typography>
                    </Stack>
                  </Stack>
                </Grid>
              ))}
            </Grid>
            {!vkycDetailsData?.data?.vkycStartTime && <PrivacyComponent />}
            {vkycDetailsData?.data?.vkycStartTime && (
              <Stack
                borderRadius={"10px"}
                bgcolor={WEB_COLORS.pale}
                p={2}
                gap={1}
              >
                <Typography color={WEB_COLORS.fadeColor}>
                  {vkycDetailsData?.data?.vkycHoliday
                    ? t("holidayNote")
                    : vkycDetailsData?.data?.vkycAgentStatus === "SCHEDULED"
                      ? t("scheduledSlot")
                      : t("workingHourNote")}{" "}
                </Typography>
                <Stack direction={"row"} alignItems={"center"}>
                  <Stack spacing={0.5}>
                    <Stack spacing={1} direction={"row"}>
                      <CalendarMonthIcon
                        sx={{ fontSize: 24, color: WEB_COLORS.primary }}
                      />
                      <Typography
                        color={"#203b34"}
                        fontWeight={500}
                        fontSize={{ xs: 14, sm: 18 }}
                      >
                        {`${dayjs(
                          new Date(vkycDetailsData?.data?.vkycStartTime),
                        ).format("h:mm A")} TO ${dayjs(
                          new Date(vkycDetailsData?.data?.vkycEndTime),
                        ).format("h:mm A")} 
                    `}
                      </Typography>
                    </Stack>
                    <Typography fontSize={12} fontWeight={400}>
                      {`${
                        vkycDetailsData?.data?.vkycAgentStatus === "SCHEDULED"
                          ? dayjs(
                              new Date(vkycDetailsData?.data?.vkycStartTime),
                            ).format("dddd, D MMMM YYYY")
                          : "Monday to Sunday"
                      }`}
                    </Typography>
                  </Stack>
                  <PrivacyComponent />
                </Stack>

                {!vkycDetailsData?.data?.scheduledDuration &&
                  vkycDetailsData?.data?.vkycAgentStatus === "SCHEDULED" && (
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      spacing={0.5}
                    >
                      <Image src={IMAGES.infoGreen} alt="Note" />
                      <Typography fontWeight={"500"}>
                        You're within our working hours. Want to proceed now?
                      </Typography>
                    </Stack>
                  )}
              </Stack>
            )}
            {/* <ButtonComponent onClick={vkycHandling}>
              {t("buttonLabel")}
            </ButtonComponent> */}
          </Stack>
        </Stack>
      </PaperComponent>
      <ModalComponent
        closeButtonRequired={false}
        title={
          vkycDetailsData?.data?.vkycAgentStatus === "SCHEDULED" &&
          vkycDetailsData?.data?.vkycApplicationStatus !== "needs_review" &&
          vkycDetailsData?.data?.vkycApplicationStatus !==
            "manually_declined" &&
          vkycDetailsData?.data?.vkycApplicationStatus !== "auto_declined" &&
          vkycDetailsData?.data?.vkycApplicationStatus !== "manually_approved"
            ? "Scheduled Slot Timing"
            : ""
        }
        open={scheduledModal}
      >
        <Stack
          alignItems={"center"}
          justifyContent={"center"}
          spacing={2}
          p={1}
        >
          <Image src={IMAGES.document} alt="Technical Issues" />
          <Typography align="center">{modalDescription}</Typography>
          <ButtonComponent
            onClick={async () => {
              await refetchVkycDetails();
              await refetchApplication();
              setScheduledModal(false);
              if (
                vkycDetailsData?.data?.vkycApplicationStatus ===
                "manually_approved"
              ) {
                if (
                  productApplication?.stage !== undefined &&
                  productApplication?.stage !== null &&
                  productApplication?.stage !== STAGE?.VKYC &&
                  productApplication?.stage !== STAGE?.VKYC_SCHEDULED
                )
                  STAGE_BASED_ROUTING(productApplication?.stage);
              } else if (
                vkycDetailsData?.data?.vkycApplicationStatus ===
                  "needs_review" ||
                vkycDetailsData?.data?.vkycApplicationStatus ===
                  "manually_declined" ||
                vkycDetailsData?.data?.vkycApplicationStatus === "auto_declined"
              ) {
                router.push("/");
              }
            }}
          >
            Ok
          </ButtonComponent>
        </Stack>
      </ModalComponent>

      <ModalAlert
        open={openModalAlert}
        showClose={false}
        onButtonClick={() => refetchApplication()}
        description={createVkycData?.data?.vkycMessage}
      />
    </Stack>
  );
};

export default VideoKyc;
